import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Valor } from '../../components/Valor';
import { ApiError, api, type Transaction } from '../../lib/api';
import { dateBR, today } from '../../lib/format';
import { CelulaCategoria, CelulaRecebedor } from '../transactions/LinhaEditavel';

/**
 * As novidades do banco, separadas por dia.
 *
 * Isto morava num bloco no topo do dashboard e virou aba propria por um
 * motivo pratico: no dashboard ele empurrava os numeros do mes para baixo
 * todo dia, e quando estava vazio deixava um buraco. Como aba, o distintivo
 * no menu avisa que ha algo, e a tela so e aberta quando ha.
 *
 * O contrato com quem le, dito na propria tela: o lancamento JA esta no
 * extrato e JA conta no saldo. Isto nao e uma fila de aprovacao — reter
 * movimentacao ate alguem clicar faria o saldo do topo mentir, e um saldo
 * errado custa mais caro que um saldo sem conferencia.
 *
 * O que muda aqui muda no lancamento na mesma hora, porque e o mesmo
 * lancamento: as celulas sao as mesmas da lista, e nao uma copia.
 */
export function NotificacoesPage() {
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const caixa = useQuery({ queryKey: ['a-conferir'], queryFn: () => api.aConferir() });
  const categorias = useQuery({ queryKey: ['categories'], queryFn: () => api.categories() });

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['a-conferir'] });
    void cliente.invalidateQueries({ queryKey: ['transactions'] });
    void cliente.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const falhou = (falha: unknown) =>
    setErro(falha instanceof ApiError ? falha.message : 'Nao consegui salvar.');

  const confirmar = useMutation({
    mutationFn: (id: string) => api.conferir(id),
    onSuccess: invalidar,
    onError: falhou,
  });

  const confirmarTudo = useMutation({
    mutationFn: () => api.conferirTudo(),
    onSuccess: invalidar,
    onError: falhou,
  });

  const dias = useMemo(() => agruparPorDia(caixa.data?.transactions ?? []), [caixa.data]);
  const total = caixa.data?.total ?? 0;
  const conferidasHoje = caixa.data?.conferidasHoje ?? [];

  return (
    <div className="stack">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Notificacoes</h1>
            <p>
              O que o banco entregou e o sistema classificou sozinho. Tudo aqui <strong>ja esta
              nos seus lancamentos</strong> e ja conta no saldo — o que falta e voce confirmar a
              categoria e quem recebeu.
            </p>
          </div>
          {total > 0 && (
            <button
              className="btn"
              disabled={confirmarTudo.isPending}
              onClick={() => confirmarTudo.mutate()}
            >
              {confirmarTudo.isPending ? 'Confirmando...' : `Conferir todas (${total})`}
            </button>
          )}
        </div>
      </header>

      {erro && <p className="alert error">{erro}</p>}

      {caixa.isLoading && <p className="muted">Carregando...</p>}

      {!caixa.isLoading && total === 0 && conferidasHoje.length === 0 && (
        <section className="card vazio">
          <h2 className="card-title">Nada por aqui hoje</h2>
          <p className="card-hint">
            O banco nao entregou movimentacao nova. Quando entregar, ela aparece aqui e o numero
            surge no menu.
          </p>
        </section>
      )}

      {dias.map((dia) => (
        <section key={dia.data} className="card">
          <div className="between">
            <h2 className="card-title">
              {dia.rotulo}
              <span className="badge">{dia.transacoes.length}</span>
            </h2>
            <span className="muted tabular">{dateBR(dia.data)}</span>
          </div>

          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>O que foi</th>
                  <th>Para quem</th>
                  <th>Categoria</th>
                  <th>Conta</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dia.transacoes.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <span className="payee-name">
                        <span>{transaction.displayName}</span>
                        {transaction.displayName !== transaction.description && (
                          <span className="payee-meta" title="Como veio no extrato">
                            {transaction.description}
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      <CelulaRecebedor transaction={transaction} />
                    </td>
                    <td>
                      <CelulaCategoria
                        transaction={transaction}
                        categorias={categorias.data?.categories ?? []}
                      />
                    </td>
                    <td className="muted">{transaction.accountName}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={transaction.amountCents} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn ghost"
                        disabled={confirmar.isPending}
                        onClick={() => confirmar.mutate(transaction.id)}
                        title="Olhei e esta certo."
                      >
                        Conferido
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {/*
        O registro do dia, ja conferido.

        Sem ele a tela esvazia no instante em que a ultima pendencia e
        confirmada, e a pergunta "o que entrou hoje?" fica sem resposta —
        que e uma pergunta diferente de "o que falta olhar?".
      */}
      {conferidasHoje.length > 0 && (
        <section className="card conferidas">
          <h2 className="card-title">
            Ja conferidas hoje
            <span className="badge">{conferidasHoje.length}</span>
          </h2>
          <p className="card-hint">
            Estao no seu extrato e voce ja confirmou. Para mudar alguma coisa, va em Lancamentos.
          </p>

          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>O que foi</th>
                  <th>Para quem</th>
                  <th>Categoria</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {conferidasHoje.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.displayName}</td>
                    <td className="muted">{transaction.counterpartyName ?? '—'}</td>
                    <td className="muted">{transaction.categoryName ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={transaction.amountCents} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {total > (caixa.data?.transactions.length ?? 0) && (
        <p className="muted">
          Mostrando {caixa.data?.transactions.length} de {total}. Confira estas e as proximas
          aparecem.
        </p>
      )}
    </div>
  );
}

interface Dia {
  data: string;
  rotulo: string;
  transacoes: Transaction[];
}

/**
 * Agrupa por data de competencia, do mais recente para o mais antigo.
 *
 * "Hoje" e "Ontem" por extenso porque sao as duas datas que alguem reconhece
 * sem calcular. Da terceira em diante o dia do mes diz mais que a contagem.
 */
function agruparPorDia(transacoes: Transaction[]): Dia[] {
  const mapa = new Map<string, Transaction[]>();

  for (const transaction of transacoes) {
    mapa.set(transaction.date, [...(mapa.get(transaction.date) ?? []), transaction]);
  }

  const hoje = today();
  const ontem = diaAnterior(hoje);

  return [...mapa.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([data, lista]) => ({
      data,
      rotulo: data === hoje ? 'Hoje' : data === ontem ? 'Ontem' : dateBR(data),
      transacoes: lista,
    }));
}

/** Aritmetica de calendario em texto: evita fuso, que aqui so atrapalha. */
function diaAnterior(data: string): string {
  const [ano, mes, dia] = data.split('-').map(Number);
  const d = new Date(Date.UTC(ano ?? 1970, (mes ?? 1) - 1, dia ?? 1));
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
